import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subscription, Observable, from, lastValueFrom } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ChatConfig, ChatMessage } from '../shared/chatbot/models/chat.model';
import {
  ChatService,
  ChatMessageProvider
} from '../shared/chatbot/services/chat.service';
import { AmplifyService } from 'app/aws/amplify.service';
import {
  SupersetApiService,
  SupersetSqlLabResponse
} from '../shared/services/superset-api/superset-api.service';
import { NotificationsService } from '../client-common/notifications/notifications.service';
import { ColorType } from '@gravity-angular/models';
import { MatDialog } from '@angular/material/dialog';
import { CreateDatasetDialogComponent } from './create-dataset-dialog/create-dataset-dialog.component';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '../../environments/environment';
import { ChatbotComponent } from 'app/shared/chatbot/chatbot.component';
import { DatabaseSelectorComponent } from './database-selector/database-selector.component';
import { CustomerSelectorComponent } from './customer-selector/customer-selector.component';
import { SharedModule } from 'app/shared/shared.module';

@Component({
  selector: 'app-sql-generator',
  templateUrl: './sql-generator.component.html',
  styleUrls: ['./sql-generator.component.scss'],
  imports: [
    ChatbotComponent,
    DatabaseSelectorComponent,
    CustomerSelectorComponent,
    SharedModule
  ],
  standalone: true
})
export class SqlGeneratorComponent implements OnInit, OnDestroy {
  // Define SQL Chatbot configuration
  chatConfig: ChatConfig = {
    title: 'SQL Generator',
    placeholder: 'Describe the SQL query you need...',
    welcomeMessage:
      'How can I help you with SQL today? Please select your customer and database from the dropdowns in the top-right corner before running queries.',
    assistantName: 'SQL Assistant',
    footerText: 'SQL Generator uses AI to create SQL queries.',
    enableSidebar: true,
    enableArchive: true,
    enableMarkdown: true,
    enableTypingAnimation: true,
    typingSpeed: 'medium',
    storageKeyPrefix: 'sql-generator',
    primaryColor: '#0085ad',
    secondaryColor: '#009fcf',
    showExecuteButton: true, // Enable SQL execute button in code blocks
    isExecuting: false // Initial execution state
  };

  // Subscriptions for cleanup
  private subscriptions: Subscription[] = [];

  // Database selection properties
  selectedDatabaseId: number = 1; // Default, will be updated when user selects a database
  selectedDatabaseName: string = ''; // Name of the selected database

  // Customer selection properties
  selectedCustomerId: string = 'walla'; // Default customer ID (WALLA)
  selectedCustomerName: string = ''; // Name of the selected customer

  // Feedback URL for SQL Generator from environment
  private readonly feedbackUrl: string = environment.sqlGeneratorFeedbackUrl;

  // Conversation management
  private conversationId: string | null = null;

  // Flag to track if a query is currently executing
  private isExecutingQuery: boolean = false;
  // Timer reference for debouncing
  private executionDebounceTimer: any = null;

  // Animated messages for SQL execution
  private sqlExecutionAnimations: string[] = [
    'Executing query<span class="dot-animation">...</span>',
    'Processing data<span class="dot-animation">...</span>',
    'Analyzing results<span class="dot-animation">...</span>',
    'Running SQL<span class="dot-animation">...</span>'
  ];

  // Animation interval reference
  private animationInterval: any = null;

  constructor(
    private chatService: ChatService,
    private http: HttpClient,
    private amplifyService: AmplifyService,
    private supersetApiService: SupersetApiService,
    private notificationsService: NotificationsService,
    private dialog: MatDialog,
    private oidcSecurityService: OidcSecurityService
  ) {}

  ngOnInit(): void {
    if (this.chatService) {
      // Register the custom message provider
      this.setupSqlMessageProvider();

      // Initialize the Superset API service
      this.initializeSupersetApi();
    }
  }

  /**
   * Initialize the Superset API service by authenticating
   */
  private async initializeSupersetApi(): Promise<void> {
    try {
      await this.supersetApiService.initialize();
      // Show toast instead of chat message
      this.showToast(
        ColorType.success,
        'SQL Connection Ready',
        'Successfully connected to the Superset',
        true
      );
    } catch (error) {
      // Show toast instead of chat message
      this.showToast(
        ColorType.danger,
        'Connection Error',
        'There was an issue connecting to the Superset. Some features may be unavailable.',
        true
      );
    }
  }

  /**
   * Clean up the animation interval to prevent memory leaks
   */
  private clearAnimationInterval(): void {
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
      this.animationInterval = null;
    }
  }

  /**
   * Component cleanup on destroy
   */
  ngOnDestroy(): void {
    // Clear animation interval if it's still running
    this.clearAnimationInterval();

    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(subscription => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }

  /**
   * Display toast notification using notification service
   */
  private showToast(
    type: ColorType,
    title: string,
    message: string,
    dismissable: boolean = true
  ): void {
    this.notificationsService.createAlert(type, title, message, dismissable);
  }

  /**
   * Setup the SQL message provider to call our API
   */
  private setupSqlMessageProvider(): void {
    // Create a custom message provider
    const sqlMessageProvider: ChatMessageProvider = {
      sendMessage: (message: string, context: any[]): Observable<any> => {
        // Return an Observable instead of a Promise
        return from(this.generateSqlQuery(message, context));
      },

      cancelRequest: () => {
        // No action needed on cancel
      }
    };

    // Register the SQL-specific message provider
    this.chatService.registerMessageProvider(sqlMessageProvider);
  }

  /**
   * Generate SQL query from natural language
   * This method handles the API call and returns a Promise
   */
  private async generateSqlQuery(
    message: string,
    context: any[]
  ): Promise<any> {
    try {
      // Generate or get conversation ID
      const conversationId = this.getOrCreateConversationId();

      // Get JWT token for Authorization header
      const token = await lastValueFrom(
        this.oidcSecurityService.getAccessToken()
      );

      // Prepare request body with new format
      const requestBody = {
        prompt: message,
        customerId: this.selectedCustomerName || 'WALLA'
      };

      // Prepare headers
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      });

      // Call the API using HTTP client
      const apiUrl = `${environment.graviteeGenAIAPI}/sql-generation`;
      const apiResponse = await lastValueFrom(
        this.http.post(apiUrl, requestBody, { headers })
      );

      // Create a formatted response in the expected ChatMessage format
      return {
        role: 'assistant',
        content: this.formatSqlResponse(apiResponse),
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Error generating SQL query:', error);
      return {
        role: 'assistant',
        content:
          'Sorry, I encountered an error generating the SQL query. Please try again.',
        timestamp: new Date()
      };
    }
  }

  /**
   * Format SQL response into a nice markdown format with enhanced SQL highlighting
   */
  private formatSqlResponse(apiResponse: any): string {
    // Check if we have an SQL query in the response or just a message
    const hasSql =
      apiResponse.hasOwnProperty('sql') &&
      typeof apiResponse.sql === 'string' &&
      apiResponse.sql.trim().length > 0;
    const content = hasSql ? apiResponse.sql : apiResponse.message || '';
    let response = '';

    // First check if explicitly marked as SQL, then verify it has proper SQL structure
    if (hasSql && this.containsSqlCommands(content)) {
      // We have valid SQL, format it as a code block and enable execution
      this.lastGeneratedSql = content;

      // Get schema from the API response or extract from SQL
      const responseSchema = apiResponse.schemaName;
      const detectedSchema =
        responseSchema || this.extractSchemaFromSql(content);

      // Format the response as markdown with a code block
      response = "Here's the SQL query based on your request:\n\n";

      // Add customer and database context
      if (this.selectedCustomerName && this.selectedDatabaseName) {
        response += `*Customer: ${this.selectedCustomerName} | Database: ${this.selectedDatabaseName}*\n\n`;
      } else if (this.selectedCustomerName) {
        response += `*Customer: ${this.selectedCustomerName}*\n\n`;
      } else if (this.selectedDatabaseName) {
        response += `*Database: ${this.selectedDatabaseName}*\n\n`;
      }

      response += '```sql\n' + content + '\n```\n\n';

      // Add schema information if available
      if (detectedSchema) {
        response += `*Using schema: \`${detectedSchema}\`*\n\n`;
      }

      // Automatically execute the query after formatting the response
      // Pass the schema from the API response if available
      setTimeout(
        () => this.executeSql(content, false, apiResponse.schemaName),
        500
      );

      // Add feedback section with Confluence link (outside the details element)
      response += this.addFeedbackSection();
    } else {
      // Either there's no SQL property or the content isn't valid SQL
      // Just show the message as plain text without code formatting
      response = content || "I couldn't generate SQL for that request.";

      // Simple error message without detailed explanation
      if (!content) {
        response = 'Unable to generate a valid SQL query for this request.';
      }

      // Add feedback section with Confluence link even for non-SQL responses
      response += this.addFeedbackSection();

      // Clear any stored SQL to prevent execution
      this.lastGeneratedSql = '';
    }

    return response;
  }

  // Store the last generated SQL query
  private lastGeneratedSql: string = '';

  /**
   * Check if the content contains actual SQL commands
   * Based on a stricter detection approach
   * @param content The content to check
   * @returns true if content appears to be valid SQL
   */
  private containsSqlCommands(content: string): boolean {
    // Basic validation first
    if (!content || typeof content !== 'string' || content.length < 10) {
      return false;
    }

    // For API responses, we want to be extremely strict to avoid falsely identifying
    // non-SQL content as SQL, which would trigger SQL execution

    // Normalize content for better pattern matching
    const normalizedContent = content.toLowerCase().replace(/\s+/g, ' ').trim();

    // Check for SQL keywords in proper context - these patterns indicate actual SQL commands
    const sqlPatterns = [
      // SELECT statements
      /\bselect\b[\s\S]+\bfrom\b[\s\S]+/i,

      // INSERT statements
      /\binsert\s+into\b[\s\S]+\bvalues\b[\s\S]*/i,
      /\binsert\s+into\b[\s\S]+\bselect\b[\s\S]*/i,

      // UPDATE statements
      /\bupdate\b[\s\S]+\bset\b[\s\S]+/i,

      // DELETE statements
      /\bdelete\s+from\b[\s\S]+/i,

      // CREATE statements
      /\bcreate\s+(?:table|view|database|schema|index)\b[\s\S]+/i,

      // ALTER statements
      /\balter\s+(?:table|view|database|schema)\b[\s\S]+/i,

      // DROP statements
      /\bdrop\s+(?:table|view|database|schema|index)\b[\s\S]+/i,

      // TRUNCATE statements
      /\btruncate\s+table\b[\s\S]+/i,

      // WITH statements (CTEs)
      /\bwith\b[\s\S]+\bas\b[\s\S]+\bselect\b[\s\S]+\bfrom\b[\s\S]+/i
    ];

    // If any SQL pattern matches, consider it SQL
    return sqlPatterns.some(pattern => pattern.test(normalizedContent));
  }

  /**
   * Handle messages received from the assistant and user
   */
  onMessageReceived(message: ChatMessage): void {
    // Check if this is a user message with a play button click
    if (message.role === 'user' && this.lastGeneratedSql) {
      const lowerContent = message.content.toLowerCase().trim();

      // Check if the user clicked the play button or explicitly asked to rerun
      if (
        lowerContent === '▶️' ||
        lowerContent.includes('rerun') ||
        lowerContent.includes('re-run') ||
        lowerContent.includes('run again') ||
        lowerContent.includes('execute again') ||
        lowerContent.includes('play') ||
        lowerContent === 'execute' ||
        lowerContent === 'run query'
      ) {
        this.executeSql(this.lastGeneratedSql);
      }
    }
  }

  /**
   * Execute a SQL query via the Superset SQL Lab API
   * @param sql The SQL query to execute
   * @param isRerun Whether this is a rerun of a previously executed query
   * @param overrideSchema Optional schema name to use for the query
   */
  public executeSql(
    sql: string,
    isRerun: boolean = false,
    overrideSchema?: string
  ): void {
    // Check if already executing a query
    if (this.isExecutingQuery) {
      this.showToast(
        ColorType.info,
        'Query in Progress',
        'Please wait until the current query finishes executing.',
        true
      );
      return;
    }

    // Clear any pending debounce timer
    if (this.executionDebounceTimer) {
      clearTimeout(this.executionDebounceTimer);
    }

    // Set a debounce timer to prevent rapid clicks
    this.executionDebounceTimer = setTimeout(() => {
      this._executeSqlImpl(sql, isRerun, overrideSchema);
    }, 300); // 300ms debounce
  }

  /**
   * Implementation for SQL query execution with proper debounce control
   */
  private _executeSqlImpl(
    sql: string,
    isRerun: boolean = false,
    overrideSchema?: string
  ): void {
    if (!sql) {
      this.sendSystemMessage('No SQL query available to execute.');
      return;
    }

    // Validate that a database is selected
    if (!this.selectedDatabaseId) {
      this.sendSystemMessage(
        '⚠️ Please select a database from the dropdown in the top-right corner before executing queries.'
      );

      this.showToast(
        ColorType.info,
        'Database Required',
        'Please select a database before executing SQL queries.',
        true
      );
      return;
    }

    // Mark that we're executing a query and update UI
    this.setExecutingState(true);

    // Create an execution message with loading status
    const executionMessageId = `sql-execution-${Date.now()}`;

    // Clean the SQL query to prevent common issues
    const cleanedSql = this.cleanSqlQuery(sql);

    // Use override schema if provided, otherwise extract from SQL
    const schema = overrideSchema || this.extractSchemaFromSql(cleanedSql);

    // Create database info text
    let dbInfo = '';
    if (this.selectedDatabaseName) {
      dbInfo = ` (Database: ${this.selectedDatabaseName}`;
      if (schema) {
        dbInfo += `, Schema: ${schema}`;
      }
      dbInfo += ')';
    } else if (schema) {
      dbInfo = ` (Schema: ${schema})`;
    }

    // Start with the first animation message
    const initialMessage = this.sqlExecutionAnimations[0] + dbInfo;
    this.sendSystemMessage(initialMessage, executionMessageId);

    // Start animation cycle for the loading message
    let animationIndex = 0;
    this.animationInterval = setInterval(() => {
      animationIndex =
        (animationIndex + 1) % this.sqlExecutionAnimations.length;
      const animatedMessage =
        this.sqlExecutionAnimations[animationIndex] + dbInfo;
      this.updateSystemMessage(executionMessageId, animatedMessage);
    }, 2000); // Change message every 2 seconds

    // Check if we need to initialize the API first (in case of auth timeout)
    this.supersetApiService
      .initialize()
      .then(() => {
        // Use the selected database ID and extracted schema
        this.supersetApiService
          .executeSql(cleanedSql, this.selectedDatabaseId, schema)
          .subscribe({
            next: response => {
              // If we have immediate success, show results
              if (response.status === 'success') {
                // Clear the animation
                this.clearAnimationInterval();

                // Format and display results
                const resultMessage = this.formatSqlResults(response);
                this.updateSystemMessage(executionMessageId, resultMessage);
                this.setExecutingState(false);
              }
              // If query is running async, get results_key and poll
              else if (
                response.status === 'running' ||
                response.status === 'pending' ||
                (response.query &&
                  (response.query.state === 'running' ||
                    response.query.state === 'pending'))
              ) {
                // Extract query ID from the response
                const queryId = this.extractQueryId(response);

                if (queryId) {
                  // The animation is already running from above

                  // First, check if we already have a results_key in the response
                  let resultsKey = null;
                  const progress =
                    response.progress ||
                    (response.query && response.query.progress) ||
                    0;

                  if (response.results_key) {
                    resultsKey = response.results_key;
                  } else if (response.query && response.query.results_key) {
                    resultsKey = response.query.results_key;
                  }

                  if (resultsKey) {
                    // If we already have results_key, use it directly
                    this.pollQueryResultsWithKey(
                      queryId,
                      resultsKey,
                      executionMessageId
                    );
                  } else {
                    // Begin the improved waiting process with progress tracking
                    this.waitForQueryCompletion(queryId, executionMessageId);
                  }
                } else {
                  this.updateSystemMessage(
                    executionMessageId,
                    'SQL query is running but no query ID was returned. Unable to track progress.'
                  );
                  this.isExecutingQuery = false;
                  // Reset the executing state in the chat config
                  this.setExecutingState(false);
                }
              } else if (response.error) {
                // Clear animation for error cases
                this.clearAnimationInterval();

                this.updateSystemMessage(
                  executionMessageId,
                  `Error executing SQL: ${response.error}`
                );
                this.isExecutingQuery = false;
                this.setExecutingState(false);
              } else if (
                !response.status &&
                response.errors &&
                response.errors.length
              ) {
                // Clear animation for error cases
                this.clearAnimationInterval();

                // Handle Superset error format which sometimes returns errors array
                this.updateSystemMessage(
                  executionMessageId,
                  `Error executing SQL: ${response.errors[0]}`
                );
                this.isExecutingQuery = false;
                this.setExecutingState(false);
              } else {
                // Clear animation for error cases
                this.clearAnimationInterval();

                this.updateSystemMessage(
                  executionMessageId,
                  'Unexpected response format. Please check the SQL syntax or try again.'
                );
                this.isExecutingQuery = false;
                this.setExecutingState(false);
              }
            },
            error: error => {
              // Clear animation for error cases
              this.clearAnimationInterval();

              // Handle different types of errors
              if (error.status === 400) {
                // Bad Request errors - try to handle with sanitized SQL if this is the first attempt
                if (!isRerun) {
                  // Show sanitizing message without animation
                  this.updateSystemMessage(
                    executionMessageId,
                    'Received Bad Request error. Attempting to run with sanitized SQL query...'
                  );

                  // Clean the SQL more aggressively to handle bad request errors
                  const sanitizedSql = this.sanitizeSqlForRetry(cleanedSql);

                  // Reset executing flag and try again with sanitized SQL
                  this.isExecutingQuery = false;

                  // Try again with sanitized SQL
                  setTimeout(() => {
                    this._executeSqlImpl(sanitizedSql, true);
                  }, 500);
                  return;
                }

                // If this is already a retry, show the error
                this.updateSystemMessage(
                  executionMessageId,
                  `Bad Request error executing SQL. Please check your SQL syntax and try again.`
                );
              } else if (error.status === 401 || error.status === 403) {
                this.updateSystemMessage(
                  executionMessageId,
                  'Authentication error when executing SQL. Please try again or refresh the page.'
                );
              } else if (error.status === 500) {
                // Extract error message from the response if available
                let errorMsg = 'Internal server error occurred';
                try {
                  if (error.error && error.error.error) {
                    errorMsg = error.error.error;
                  } else if (typeof error.error === 'string') {
                    errorMsg = error.error;
                  }
                } catch (e) {
                  // Error parsing response - use default message
                }

                this.updateSystemMessage(
                  executionMessageId,
                  `Error executing SQL: ${errorMsg}`
                );
              } else {
                this.updateSystemMessage(
                  executionMessageId,
                  `Error executing SQL: ${
                    error.message || error.statusText || 'Unknown error'
                  }`
                );
              }

              // Clear animation on error
              this.clearAnimationInterval();

              // Always reset the executing flag on error
              this.isExecutingQuery = false;
              this.setExecutingState(false);
            }
          });
      })
      .catch(error => {
        // Clear animation on initialization error
        this.clearAnimationInterval();

        this.updateSystemMessage(
          executionMessageId,
          'Could not connect to SQL execution service. Please try again later or contact support.'
        );
        this.isExecutingQuery = false;
        this.setExecutingState(false);
      });
  }

  /**
   * Poll for results using the results_key
   * @param queryId The original query ID (for fallback)
   * @param resultsKey The results key to use
   * @param messageId The message ID to update
   * @param attempts Current attempt count
   */
  private pollQueryResultsWithKey(
    queryId: string,
    resultsKey: string,
    messageId: string,
    attempts: number = 0
  ): void {
    // Maximum attempts with animated loading (2 seconds per attempt)
    // 30 attempts = 60 seconds maximum wait time
    if (attempts > 30) {
      // Clear the animation interval
      this.clearAnimationInterval();

      // Show timeout message
      this.updateSystemMessage(
        messageId,
        'Query is taking longer than expected. You can check the results in Superset SQL Lab.'
      );
      this.setExecutingState(false);
      return;
    }

    // Wait for 2 seconds before polling
    setTimeout(() => {
      // Use the dedicated results_key endpoint - simplified approach with no fallbacks
      this.supersetApiService.getQueryResultsByKey(resultsKey).subscribe({
        next: response => {
          try {
            // Only two possible states: success or still running

            // Success - we have data to show
            if (response.status === 'success' || response.data) {
              // Clear the animation interval
              this.clearAnimationInterval();

              // Format and display results
              const resultMessage = this.formatSqlResults(response);
              this.updateSystemMessage(messageId, resultMessage);
              this.isExecutingQuery = false;
              this.setExecutingState(false);
            }
            // Error reported
            else if (response.status === 'failed' || response.error) {
              // Clear the animation interval
              this.clearAnimationInterval();

              const errorMsg = response.error || 'Query execution failed';
              this.updateSystemMessage(
                messageId,
                `Error executing SQL: ${errorMsg}`
              );
              this.isExecutingQuery = false;
              this.setExecutingState(false);
            }
            // Still running - continue polling
            else {
              // Animation is already running from _executeSqlImpl
              // Continue polling
              this.pollQueryResultsWithKey(
                queryId,
                resultsKey,
                messageId,
                attempts + 1
              );
            }
          } catch (error) {
            // Clear the animation interval
            this.clearAnimationInterval();

            // Handle any processing errors
            this.updateSystemMessage(
              messageId,
              `Error processing results: ${error.message}`
            );
            this.isExecutingQuery = false;
            this.setExecutingState(false);
          }
        },
        error: error => {
          // Two possible cases for errors:

          // 1. Not found (404) - results might not be ready yet, continue polling
          if (error.status === 404 && attempts < 10) {
            // Continue polling - animation is already running
            this.pollQueryResultsWithKey(
              queryId,
              resultsKey,
              messageId,
              attempts + 1
            );
          }
          // 2. Other error - display it
          else {
            // Clear the animation interval
            this.clearAnimationInterval();

            // Format error message
            let errorMsg = error.message || error.statusText || 'Unknown error';
            // Try to extract a more detailed error message from the response
            try {
              if (error.error) {
                if (typeof error.error === 'string') {
                  errorMsg = error.error;
                } else if (error.error.error) {
                  errorMsg = error.error.error;
                } else if (error.error.message) {
                  errorMsg = error.error.message;
                }
              }
            } catch (parseError) {
              console.error('Error parsing error response:', parseError);
            }

            this.updateSystemMessage(
              messageId,
              `Error retrieving query results: ${errorMsg}`
            );
            this.isExecutingQuery = false; // Reset executing flag on error
            // Reset the executing state in the chat config
            this.setExecutingState(false);
          }
        }
      });
    }, 2000); // Poll every 2 seconds
  }

  /**
   * Poll for results of an asynchronous SQL query using query ID
   * Always follows the correct flow: get query details for results_key, then use results_key for results
   * @param queryId The ID of the query to poll for results
   * @param messageId The message ID to update with results
   * @param attempts Current attempt count
   */
  private pollQueryResults(
    queryId: string,
    messageId: string,
    attempts: number = 0
  ): void {
    // Increase max attempts to give more time for query execution
    if (attempts > 30) {
      // Allow up to 60 seconds (30 attempts * 2 second interval)
      this.updateSystemMessage(
        messageId,
        'Query is taking longer than expected. You can check the results in Superset SQL Lab.'
      );
      this.setExecutingState(false); // Reset executing flag when giving up
      // Reset the executing state in the chat config
      this.setExecutingState(false);
      return;
    }

    // Wait for 2 seconds before polling
    setTimeout(() => {
      // Use the GET endpoint which will:
      // 1. First get query details to extract results_key
      // 2. Then fetch results using that key
      this.supersetApiService.getQueryResultsGet(queryId).subscribe({
        next: response => {
          try {
            // Handle different response formats
            if (response.status === 'success') {
              const resultMessage = this.formatSqlResults(response);
              this.updateSystemMessage(messageId, resultMessage);
              this.isExecutingQuery = false; // Reset executing flag on success
              // Reset the executing state in the chat config
              this.setExecutingState(false);
            } else if (
              response.status === 'running' ||
              response.status === 'pending' ||
              (response.query && response.query.state === 'running') ||
              (response.query && response.query.state === 'pending')
            ) {
              // Update message to show progress
              this.updateSystemMessage(
                messageId,
                `Executing SQL query... (${(attempts + 1) * 2}s)`
              );
              // Continue polling
              this.pollQueryResults(queryId, messageId, attempts + 1);
            } else if (response.status === 'failed' || response.error) {
              const errorMsg = response.error || 'Query execution failed';
              this.updateSystemMessage(
                messageId,
                `Error executing SQL: ${errorMsg}`
              );
              this.isExecutingQuery = false; // Reset executing flag on error
              // Reset the executing state in the chat config
              this.setExecutingState(false);
            } else if (response.data) {
              // We have data but no explicit success status, try to format it
              const resultMessage = this.formatSqlResults(response);
              this.updateSystemMessage(messageId, resultMessage);
              this.isExecutingQuery = false; // Reset executing flag on success
              // Reset the executing state in the chat config
              this.setExecutingState(false);
            } else {
              // Handle any other unexpected statuses
              // Continue polling for a while, but be careful not to loop endlessly
              if (attempts < 10) {
                this.pollQueryResults(queryId, messageId, attempts + 1);
              } else {
                this.updateSystemMessage(
                  messageId,
                  'Unable to get a valid response after multiple attempts. The query may have failed silently.'
                );
                this.isExecutingQuery = false; // Reset executing flag after enough attempts
                // Reset the executing state in the chat config
                this.setExecutingState(false);
              }
            }
          } catch (error) {
            this.updateSystemMessage(
              messageId,
              `Error processing results: ${error.message}`
            );
            this.isExecutingQuery = false; // Reset executing flag on error
            // Reset the executing state in the chat config
            this.setExecutingState(false);
          }
        },
        error: error => {
          // Handle error in the logic below

          // For some errors, it's better to keep polling
          if (error.status === 404 && attempts < 5) {
            // 404 might mean results aren't ready yet, keep trying
            this.updateSystemMessage(
              messageId,
              `Executing SQL query... (${(attempts + 1) * 2}s)`
            );
            this.pollQueryResults(queryId, messageId, attempts + 1);
          } else {
            let errorMsg = error.message || error.statusText || 'Unknown error';
            // Try to extract a more detailed error message from the response
            try {
              if (error.error) {
                if (typeof error.error === 'string') {
                  errorMsg = error.error;
                } else if (error.error.error) {
                  errorMsg = error.error.error;
                } else if (error.error.message) {
                  errorMsg = error.error.message;
                }
              }
            } catch (parseError) {
              console.error('Error parsing error response:', parseError);
            }

            this.updateSystemMessage(
              messageId,
              `Error retrieving query results: ${errorMsg}`
            );
            this.isExecutingQuery = false; // Reset executing flag on error
            // Reset the executing state in the chat config
            this.setExecutingState(false);
          }
        }
      });
    }, 2000); // Poll every 2 seconds
  }

  /**
   * Wait for query completion by repeatedly checking the query status
   * This method will:
   * 1. Show animated loading messages
   * 2. Check status and progress
   * 3. Wait for results_key
   * 4. Call results API with the key when ready
   *
   * @param queryId The ID of the query to track
   * @param messageId The message ID to update with progress and results
   * @param attempts Optional number of attempts already made
   */
  private waitForQueryCompletion(
    queryId: string,
    messageId: string,
    attempts: number = 0
  ): void {
    // Maximum attempts (2 seconds between attempts)
    const maxAttempts = 30; // 60 seconds total

    if (attempts > maxAttempts) {
      this.clearAnimationInterval();
      this.updateSystemMessage(
        messageId,
        'Query is taking longer than expected. You can check the results in Superset SQL Lab.'
      );
      this.isExecutingQuery = false;
      this.setExecutingState(false);
      return;
    }

    // Wait 2 seconds between attempts
    setTimeout(() => {
      // Call the query details API
      this.supersetApiService.getQueryDetails(queryId).subscribe({
        next: queryDetails => {
          try {
            // Extract status, progress and results_key
            const status =
              queryDetails.status ||
              (queryDetails.query && queryDetails.query.status) ||
              queryDetails.state;

            const progress =
              queryDetails.progress ||
              (queryDetails.query && queryDetails.query.progress) ||
              0;

            let resultsKey = this.extractResultsKey(queryDetails);

            // Update animation message with progress if available
            if (progress > 0) {
              const animationIndex =
                attempts % this.sqlExecutionAnimations.length;
              const baseMessage = this.sqlExecutionAnimations[
                animationIndex
              ].replace('<span class="dot-animation">...</span>', '');
              const progressMessage = `${baseMessage} (${progress}%)<span class="dot-animation">...</span>`;
              this.updateSystemMessage(messageId, progressMessage);
            }

            // Check if query is complete
            if (status === 'success' && (progress === 100 || resultsKey)) {
              // Clear animation
              this.clearAnimationInterval();

              if (resultsKey) {
                // Use the results_key to get results
                this.updateSystemMessage(
                  messageId,
                  'Query completed. Fetching results...'
                );
                this.pollQueryResultsWithKey(queryId, resultsKey, messageId);
              } else {
                // Try to format results directly if they're included in the response
                if (queryDetails.data) {
                  const resultMessage = this.formatSqlResults(queryDetails);
                  this.updateSystemMessage(messageId, resultMessage);
                  this.setExecutingState(false);
                } else {
                  // No results key and no data
                  this.updateSystemMessage(
                    messageId,
                    'Query completed but no results found. Please try again.'
                  );
                  this.setExecutingState(false);
                }
              }
            }
            // Check if query failed
            else if (status === 'failed' || status === 'error') {
              this.clearAnimationInterval();
              const errorMsg = queryDetails.error || 'Query execution failed';
              this.updateSystemMessage(
                messageId,
                `Error executing SQL: ${errorMsg}`
              );
              this.isExecutingQuery = false;
              this.setExecutingState(false);
            }
            // If we have a results key but query is still running
            else if (resultsKey) {
              // We have a results_key while query is still running
              // This is good - we can start polling with the key
              this.pollQueryResultsWithKey(queryId, resultsKey, messageId);
            }
            // Still running, continue waiting
            else {
              // Continue waiting
              this.waitForQueryCompletion(queryId, messageId, attempts + 1);
            }
          } catch (error) {
            this.clearAnimationInterval();
            this.updateSystemMessage(
              messageId,
              `Error processing query details: ${error.message}`
            );
            this.isExecutingQuery = false;
            this.setExecutingState(false);
          }
        },
        error: error => {
          // If 404, query might not be registered yet, keep trying for a reasonable number of attempts
          if (error.status === 404 && attempts < 5) {
            // Continue waiting
            this.waitForQueryCompletion(queryId, messageId, attempts + 1);
          } else {
            this.clearAnimationInterval();
            let errorMsg = error.message || error.statusText || 'Unknown error';
            this.updateSystemMessage(
              messageId,
              `Error tracking query: ${errorMsg}`
            );
            this.isExecutingQuery = false;
            this.setExecutingState(false);
          }
        }
      });
    }, 2000); // Check every 2 seconds
  }

  /**
   * Helper method to escape HTML special characters to prevent XSS
   * when displaying user data in HTML context
   */
  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Format SQL query results into a readable message
   */
  private formatSqlResults(response: SupersetSqlLabResponse): string {
    // SQL Lab might have different response formats, handle all cases
    let columns: Array<{ name: string; type: string }> = [];
    let data: any[] = [];
    let rowCount = 0;
    let schema = '';
    try {
      // Handle various result formats
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array of data
          data = response.data;
          rowCount = data.length;
        } else if (typeof response.data === 'object') {
          if (response.data.data && Array.isArray(response.data.data)) {
            // Sometimes the data is nested one level deeper
            data = response.data.data;
            rowCount = data.length;

            // If we have nested data, we might also have nested columns
            if (response.data.columns && Array.isArray(response.data.columns)) {
              columns = response.data.columns;
            }
          } else {
            // Handle case where data is a single object or different format
            // Try to extract data from other fields
            if ('result' in response.data) {
              const result = response.data.result;
              if (Array.isArray(result)) {
                data = result;
                rowCount = result.length;
              }
            }
          }
        }
      }

      // If columns weren't found in nested data, check top level
      if (
        columns.length === 0 &&
        response.columns &&
        Array.isArray(response.columns)
      ) {
        columns = response.columns;
      }

      // Get schema information if available - check multiple locations
      if (response.query) {
        schema = response.query.schema || '';
      } else if (
        response.data &&
        response.data.query &&
        response.data.query.schema
      ) {
        schema = response.data.query.schema;
      }

      // If we still don't have columns but have data, try to infer columns from first data row
      if (columns.length === 0 && data.length > 0) {
        columns = Object.keys(data[0]).map(key => ({
          name: key,
          type: typeof data[0][key] === 'number' ? 'number' : 'string'
        }));
      }
    } catch (error) {
      return `Error formatting SQL results: ${
        error.message
      }.\n\nRaw response: ${JSON.stringify(response, null, 2)}`;
    }

    // Create a collapsible results section
    const resultRowCount = data ? data.length : 0;
    const rowsText = resultRowCount === 1 ? 'row' : 'rows';

    // Start with collapsible results header
    let formattedMessage = `<details class="results-container">\n`;
    formattedMessage += `<summary class="results-summary">### Results (${resultRowCount} ${rowsText}) - Click to expand</summary>\n\n`;

    // Add schema info in a compact format if available
    if (schema) {
      formattedMessage += `*Schema: \`${schema}\`* | `;
    }

    // Check if we have valid columns
    if (!columns || columns.length === 0) {
      formattedMessage +=
        'Query executed successfully. No results structure returned.</details>';
      return formattedMessage;
    }

    // Calculate appropriate height class based on row count
    const heightClass =
      resultRowCount <= 1
        ? 'single-row'
        : resultRowCount <= 5
        ? 'few-rows'
        : resultRowCount <= 15
        ? 'medium-rows'
        : 'many-rows';

    // Direct scrollable table without any wrapper or headers, with dynamic height class
    formattedMessage += `<div class="scrollable-results-table ${heightClass}">\n\n`;

    // Create the table with HTML structure optimized for auto-width columns
    formattedMessage +=
      '<table class="sql-results-table" cellspacing="0" cellpadding="0">\n';

    // Add column group to help with column width control
    formattedMessage += '<colgroup>\n';
    columns.forEach(() => {
      // Let each column width be determined by content
      formattedMessage += '<col style="width: auto;" />\n';
    });
    formattedMessage += '</colgroup>\n';

    formattedMessage += '<thead>\n<tr>\n';

    // Add headers with data attributes to help with styling
    columns.forEach((col, index) => {
      const colName = this.escapeHtml(col.name);
      formattedMessage += `<th data-index="${index}">${colName}</th>\n`;
    });

    formattedMessage += '</tr>\n</thead>\n<tbody>\n';

    // Display all rows - the scrollable-results-table class will handle overflow
    if (data && data.length > 0) {
      // Only display up to 1000 rows to prevent browser performance issues
      // with extremely large result sets
      const maxRowsToDisplay = Math.min(1000, data.length);
      const rowsToDisplay = data.slice(0, maxRowsToDisplay);

      rowsToDisplay.forEach(row => {
        formattedMessage += '<tr>\n';

        columns.forEach(col => {
          const value = row[col.name];
          // Display null as NULL but otherwise show raw values exactly as they are
          const displayValue = value === null ? 'NULL' : String(value);

          // Display cell with data attributes to help with styling and auto-width
          const cellWidth = displayValue.length > 20 ? 'wider' : 'normal';
          formattedMessage += `<td data-width="${cellWidth}" title="${this.escapeHtml(
            displayValue
          )}">${this.escapeHtml(displayValue)}</td>\n`;
        });

        formattedMessage += '</tr>\n';
      });

      // Close the table and scrollable container
      formattedMessage += '</tbody>\n</table>\n';
      formattedMessage += '</div>\n\n'; // Close scrollable-results-table

      // Add a simple message at the end with a play button for rerunning
      formattedMessage += '\n*Click 🔄 to run this query again*';
    } else {
      formattedMessage +=
        'Query executed successfully, but returned no data rows.';
    }

    // Close the details tag for the collapsible container
    formattedMessage += '\n</details>';

    return formattedMessage;
  }

  /**
   * Send a system message to the chat
   * @param content The message content
   * @param messageId Optional message ID for later updates
   */
  private sendSystemMessage(content: string, messageId?: string): void {
    const systemMessage: ChatMessage = {
      role: 'assistant',
      content: content,
      timestamp: new Date(),
      id: messageId || `msg-${Date.now()}`
    };

    // We're directly pushing to the chat service
    if (this.chatService) {
      this.chatService['messagesSubject'].getValue().push(systemMessage);
      this.chatService['messagesSubject'].next(
        this.chatService['messagesSubject'].getValue()
      );
    }
  }

  /**
   * Update an existing system message by ID
   * @param messageId The ID of the message to update
   * @param content The new content for the message
   */
  private updateSystemMessage(messageId: string, content: string): void {
    if (this.chatService) {
      const messages = this.chatService['messagesSubject'].getValue();
      const messageIndex = messages.findIndex(m => m.id === messageId);

      if (messageIndex !== -1) {
        messages[messageIndex].content = content;
        this.chatService['messagesSubject'].next([...messages]); // Create a new array to trigger change detection
      } else {
        // If message not found, send as a new message
        this.sendSystemMessage(content);
      }
    }
  }

  /**
   * Handle play button clicks from chatbot messages
   * This allows us to trigger SQL execution when the user clicks on the play button emoji
   */
  onPlayButtonClicked(event: any): void {
    if (event && event.content) {
      // Extract SQL code blocks from the message content
      const sqlMatch = event.content.match(/```sql\n([\s\S]*?)\n```/);

      if (sqlMatch && sqlMatch[1]) {
        const sqlCode = sqlMatch[1].trim();
        this.executeSql(sqlCode);
      }
    }
  }

  /**
   * Handle when user clicks on the create dataset button in a SQL code block
   */
  onCreateDatasetClicked(event: { message: any; sql: string }): void {
    if (event && event.sql) {
      this.createVirtualDataset(event.sql);
    }
  }

  /**
   * Handle database selection event from the database selector
   * @param database The selected database info with ID and name
   */
  onDatabaseSelected(database: { id: number; name: string }): void {
    this.selectedDatabaseId = database.id;
    this.selectedDatabaseName = database.name;

    if (database.id) {
      this.showToast(
        ColorType.success,
        'Database Selected',
        `Switched to database "${database.name}". Ready for queries.`,
        true
      );
    }
  }

  /**
   * Handle customer selection event from the customer selector
   * @param customer The selected customer info with ID and name
   */
  onCustomerSelected(customer: { id: string; name: string }): void {
    this.selectedCustomerId = customer.id;
    this.selectedCustomerName = customer.name;

    // Reset conversation when customer changes to start fresh context
    this.resetConversation();

    if (customer.id) {
      this.showToast(
        ColorType.info,
        'Customer Selected',
        `Switched to customer "${customer.name}". You can now generate queries for this customer.`,
        true
      );
    }
  }

  /**
   * Fetch results for a specific query ID
   * This method always follows the correct flow: get query details to extract results_key, then use results_key to get results
   * @param queryId The ID of the query to get results for
   * @param messageIdToUpdate Optional message ID to update with results
   */
  public getQueryResults(queryId: string, messageIdToUpdate?: string): void {
    if (!queryId) {
      return;
    }

    const messageId = messageIdToUpdate || `sql-results-${Date.now()}`;

    if (!messageIdToUpdate) {
      // If no message ID was provided, create a new message
      this.sendSystemMessage('Retrieving SQL query results...', messageId);
    }

    // Use the correct API flow: always call getQueryResultsGet which will:
    // 1. Get query details from the query API to extract results_key
    // 2. Use the results_key to call the results API
    this.supersetApiService.getQueryResultsGet(queryId).subscribe({
      next: response => {
        this.handleResultsResponse(response, queryId, messageId);
      },
      error: error => {
        this.updateSystemMessage(
          messageId,
          `Error retrieving query results: ${
            error.message || error.statusText || 'Unknown error'
          }`
        );
      }
    });
  }

  // Method removed as it was unused

  /**
   * Handle results response common logic
   * @param response The query results response
   * @param queryId The query ID
   * @param messageId The message ID to update
   */
  private handleResultsResponse(
    response: SupersetSqlLabResponse,
    queryId: string,
    messageId: string
  ): void {
    if (response.status === 'success') {
      const resultMessage = this.formatSqlResults(response);
      this.updateSystemMessage(messageId, resultMessage);
    } else if (response.status === 'running' || response.status === 'pending') {
      // Query is still running, start polling
      this.updateSystemMessage(messageId, 'SQL query is still running...');
      this.pollQueryResults(queryId, messageId);
    } else if (response.error) {
      this.updateSystemMessage(
        messageId,
        `Error retrieving query results: ${response.error}`
      );
    } else if (response.data) {
      // We have data but no explicit success status, try to format it
      const resultMessage = this.formatSqlResults(response);
      this.updateSystemMessage(messageId, resultMessage);
    } else {
      this.updateSystemMessage(
        messageId,
        'Unable to retrieve query results. The query may have expired or failed.'
      );
    }
  }

  /**
   * Create a virtual dataset from SQL query
   * @param sqlQuery The SQL query to use for the dataset
   */
  createVirtualDataset(sqlQuery: string): void {
    if (!sqlQuery) {
      this.showToast(
        ColorType.danger, // Using danger as warning may not be available
        'Missing SQL',
        'No SQL query available to create dataset.',
        true
      );
      return;
    }

    // Validate that a database is selected
    if (!this.selectedDatabaseId) {
      this.showToast(
        ColorType.info,
        'Database Required',
        'Please select a database from the dropdown in the top-right corner before creating a dataset.',
        true
      );
      return;
    }

    // Clean the SQL query
    const cleanedSql = this.cleanSqlQuery
      ? this.cleanSqlQuery(sqlQuery)
      : sqlQuery;

    // Extract schema from SQL if present
    const schema = this.extractSchemaFromSql(cleanedSql);

    // Open dialog to get dataset name from user
    this.openCreateDatasetDialog(cleanedSql, schema);
  }

  /**
   * Opens a dialog to get the dataset name from the user
   * @param sqlQuery The SQL query to use for the dataset
   * @param schema Optional schema to use
   */
  private openCreateDatasetDialog(sqlQuery: string, schema?: string): void {
    try {
      const datasetDialogRef = this.dialog.open(CreateDatasetDialogComponent, {
        width: '500px',
        data: {
          sqlQuery,
          schema,
          databaseId: this.selectedDatabaseId,
          databaseName: this.selectedDatabaseName
        }
      });

      // Subscribe to the dialog result
      datasetDialogRef.afterClosed().subscribe(result => {
        if (result && result.datasetName) {
          this.submitDatasetCreation(result.datasetName, sqlQuery, schema);
        }
      });
    } catch (error) {
      // Show error toast
      this.showToast(
        ColorType.danger,
        'Dialog Error',
        'Could not open dataset dialog: ' + (error.message || 'Unknown error'),
        true
      );
    }

    // The original dialog reference and subscribe block is no longer needed
    // as it's now handled inside the setTimeout block
  }

  /**
   * Submit the dataset creation to the API
   * @param datasetName The name for the dataset
   * @param sqlQuery The SQL query that defines the dataset
   * @param schema Optional schema name
   */
  private submitDatasetCreation(
    datasetName: string,
    sqlQuery: string,
    schema?: string
  ): void {
    // Show loading toast
    const toastId = this.showToast(
      ColorType.info,
      'Creating Dataset',
      `Creating dataset "${datasetName}"...`,
      false
    );

    // First ensure the API is initialized
    this.supersetApiService
      .initialize()
      .then(() => {
        // Create the dataset using the API
        this.createVirtualDatasetCall(
          datasetName,
          sqlQuery,
          this.selectedDatabaseId,
          schema
        ).subscribe({
          next: response => {
            // Show success toast
            this.showToast(
              ColorType.success,
              'Dataset Created',
              `Dataset "${datasetName}" was created successfully.`,
              true
            );

            // Send confirmation message in chat
            this.sendSystemMessage(
              `✅ **Dataset Created**: The virtual dataset "${datasetName}" has been successfully created in database "${this.selectedDatabaseName}".`
            );
          },
          error: error => {
            // Show error toast
            this.showToast(
              ColorType.danger,
              'Dataset Creation Failed',
              `Could not create dataset: ${error.message || 'Unknown error'}`,
              true
            );

            // Send error message in chat
            this.sendSystemMessage(
              `❌ **Error**: Failed to create dataset "${datasetName}". ${
                error.message || 'Unknown error'
              }`
            );
          }
        });
      })
      .catch(error => {
        // Show error toast
        this.showToast(
          ColorType.danger,
          'Connection Error',
          `Could not connect to Superset: ${error.message || 'Unknown error'}`,
          true
        );
      });
  }

  /**
   * Create a virtual dataset from an SQL query
   * This is a direct implementation of the API call to create a virtual dataset
   */
  private createVirtualDatasetCall(
    datasetName: string,
    sqlQuery: string,
    databaseId: number,
    schema?: string
  ): Observable<any> {
    const url = `${this.supersetApiService['baseUrl']}/api/v1/dataset/`;

    // Create dataset payload according to Superset API
    const payload = {
      database: databaseId,
      schema: schema || null,
      table_name: datasetName,
      sql: sqlQuery,
      owners: [],
      is_managed_externally: false,
      external_url: null
    };

    return from(this.supersetApiService['getAuthHeaders']()).pipe(
      switchMap(headers => {
        return this.supersetApiService['http']
          .post<any>(url, payload, { headers })
          .pipe(
            catchError(error => {
              // If we get a 401 Unauthorized error, try to re-authenticate and retry once
              if (error.status === 401) {
                return from(this.supersetApiService['accessLogin']()).pipe(
                  switchMap(() => this.supersetApiService['getAuthHeaders']()),
                  switchMap(newHeaders => {
                    return this.supersetApiService['http'].post<any>(
                      url,
                      payload,
                      { headers: newHeaders }
                    );
                  })
                );
              }

              // Re-throw the error for other error types
              throw error;
            })
          );
      })
    );
  }

  /**
   * Clean the SQL query to fix common issues
   * @param sql The SQL query to clean
   * @returns Cleaned SQL query
   */
  private cleanSqlQuery(sql: string): string {
    if (!sql) return '';

    // Remove SQL comments (both single line and multi-line)
    let cleanedSql = sql.replace(/--.*$/gm, '');
    cleanedSql = cleanedSql.replace(/\/\*[\s\S]*?\*\//g, '');

    // Remove multiple spaces
    cleanedSql = cleanedSql.replace(/\s+/g, ' ');

    // Remove trailing semicolons as they can cause issues with some DBs
    cleanedSql = cleanedSql.replace(/;\s*$/, '');

    return cleanedSql.trim();
  }

  /**
   * Extract schema from SQL query if it's specified with FROM schema.table syntax
   * @param sql The SQL query to analyze
   * @returns Schema name or empty string if not found
   */
  private extractSchemaFromSql(sql: string): string {
    if (!sql) return '';

    // Lowercase for case-insensitive matching
    const lowerSql = sql.toLowerCase();

    // Look for FROM clause with schema.table pattern
    const fromMatch = lowerSql.match(/from\s+([a-z0-9_]+)\.([a-z0-9_]+)/i);
    if (fromMatch && fromMatch[1]) {
      return fromMatch[1];
    }

    // Look for JOIN clause with schema.table pattern
    const joinMatch = lowerSql.match(/join\s+([a-z0-9_]+)\.([a-z0-9_]+)/i);
    if (joinMatch && joinMatch[1]) {
      return joinMatch[1];
    }

    return '';
  }

  /**
   * Sanitize SQL for retry - more aggressive cleanup for bad request errors
   * @param sql The SQL query to sanitize
   * @returns Sanitized SQL query
   */
  private sanitizeSqlForRetry(sql: string): string {
    if (!sql) return '';

    // Start with regular cleaning
    let sanitized = this.cleanSqlQuery(sql);

    // Remove any comments
    sanitized = sanitized.replace(/--.*$/gm, '');
    sanitized = sanitized.replace(/\/\*[\s\S]*?\*\//g, '');

    // Normalize quotes
    sanitized = sanitized.replace(/[""'']/g, "'");

    // Normalize whitespace
    sanitized = sanitized.replace(/\s+/g, ' ').trim();

    return sanitized;
  }

  /**
   * Set the executing state flag and update the chat config
   * @param isExecuting True if executing, false otherwise
   */
  private setExecutingState(isExecuting: boolean): void {
    this.isExecutingQuery = isExecuting;

    // Update the chat config
    if (this.chatConfig) {
      this.chatConfig.isExecuting = isExecuting;
    }
  }

  /**
   * Extract query ID from different response formats
   * @param response The response object to extract from
   * @returns The query ID or undefined if not found
   */
  private extractQueryId(response: any): string | undefined {
    if (!response) return undefined;

    // Direct path
    if (response.queryId) return response.queryId.toString();

    // Nested in query object
    if (response.query) {
      if (response.query.queryId) return response.query.queryId.toString();
    }

    // Nested in result object
    if (response.result) {
      if (response.result.queryId) return response.result.queryId.toString();
    }

    return undefined;
  }

  /**
   * Extract the results key from query details
   * @param queryDetails The query details object from API
   * @returns results_key string or undefined if not found
   */
  private extractResultsKey(queryDetails: any): string | undefined {
    if (!queryDetails) return undefined;

    // Direct paths
    if (queryDetails.results_key) return queryDetails.results_key;
    if (queryDetails.resultsKey) return queryDetails.resultsKey;

    // Nested in result object
    if (queryDetails.result) {
      if (queryDetails.result.results_key)
        return queryDetails.result.results_key;
      if (queryDetails.result.resultsKey) return queryDetails.result.resultsKey;
    }

    // Nested in query object
    if (queryDetails.query && queryDetails.query.results_key) {
      return queryDetails.query.results_key;
    }

    // Nested in data object
    if (queryDetails.data && typeof queryDetails.data === 'object') {
      if (queryDetails.data.results_key) return queryDetails.data.results_key;
      if (queryDetails.data.query && queryDetails.data.query.results_key) {
        return queryDetails.data.query.results_key;
      }
    }

    return undefined;
  }

  /**
   * Get authorization token for API calls
   * Note: Currently not used as AWS Amplify handles authentication automatically
   * @returns Promise<string> authorization token
   */
  private async getAuthorizationToken(): Promise<string> {
    try {
      // Get the token from OIDC security service (same way as AmplifyService does)
      const source$ = this.amplifyService.oidcSecurityService.getAccessToken();
      const token = await lastValueFrom(source$);

      if (token) {
        return `Bearer ${token}`;
      }

      // Fallback - return empty string
      console.warn('No access token available');
      return '';
    } catch (error) {
      console.warn('Could not retrieve authorization token:', error);
      // Return empty string as fallback - API might handle missing auth
      return '';
    }
  }

  /**
   * Get or create a conversation ID for API calls
   * @returns conversation ID string
   */
  private getOrCreateConversationId(): string {
    if (!this.conversationId) {
      // Generate a new conversation ID - you can customize this logic
      this.conversationId = `conv_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 9)}`;
    }
    return this.conversationId;
  }

  /**
   * Adds a feedback section with Confluence link to the response
   * @returns formatted string with feedback section
   */
  private addFeedbackSection(): string {
    // Create a feedback section with a horizontal rule to separate it
    let feedbackSection = '\n\n---\n\n';
    feedbackSection += '### 📝 Feedback\n';

    // Use the direct Confluence URL without query parameters
    feedbackSection += `Was this SQL helpful? [Click here to provide feedback](${this.feedbackUrl})`;

    return feedbackSection;
  }

  /**
   * Reset conversation ID - call this when starting a new conversation
   */
  public resetConversation(): void {
    this.conversationId = null;
  }
}
